import { TILE } from '../config/tuning'
import type { ArmamentId, Entity } from '../ecs/entity'
import type { World } from '../ecs/world'
import { makeProjectile } from '../entities/templates'
import { PALETTE } from '../render/palette'
import { Tile, isTeleporter } from '../world/tiles'
import { hurtPlayer, killEntity } from './damage'

/**
 * Player system: intent → movement/aim/fire/magic/interact, plus the tile
 * effects the player triggers (doors, traps, teleporters, exit).
 */
export function playerSystem(world: World, dt: number): void {
  const t = world.tuning
  for (const p of world.players()) {
    const ps = p.player!
    const intent = ps.source.poll(p.x, p.y)
    ps.intent = intent

    // movement (attrition means standing still is never safe; speed is flat)
    const dx = intent.move.x * t.player.speed * dt
    const dy = intent.move.y * t.player.speed * dt
    openAdjacentDoors(world, p, dx, dy)
    const assist = doorAssist(world, p, dx, dy, dt)
    world.map.moveCircle(p, dx + assist.x, dy + assist.y, p.wallClipped)
    p.facing = Math.atan2(intent.aim.y, intent.aim.x)

    // timers
    if (p.invuln && p.invuln > 0) p.invuln -= dt
    if (ps.fireT > 0) ps.fireT -= dt
    if (ps.contactT > 0) ps.contactT -= dt
    if (ps.altT > 0) ps.altT -= dt

    // fire (hold-to-autofire). Requisitioned mods apply to the standard-issue
    // blaster only; found armaments use their fixed profiles and burn charge.
    if (intent.fire && ps.fireT <= 0) {
      const muzzle = p.radius + 6
      const mx = p.x + intent.aim.x * muzzle
      const my = p.y + intent.aim.y * muzzle
      const arm = ps.armament
      if (arm) {
        fireArmament(world, arm.id, mx, my, intent.aim.x, intent.aim.y)
        ps.fireT = t.armaments[arm.id].fireCooldown
        arm.charge--
        if (arm.charge <= 0) {
          ps.armament = undefined
          world.bus.emit('armament_exhausted', { id: arm.id })
        }
      } else {
        ps.fireT = t.player.fireCooldown * world.mods.fireCooldownMul
        world.add(
          makeProjectile(
            t,
            mx,
            my,
            intent.aim.x,
            intent.aim.y,
            t.player.projectileSpeed,
            Math.round(t.player.projectileDamage * world.mods.damageMul),
            'participant'
          )
        )
      }
      // muzzle flash + tiny kick so firing has weight (burst/shake self-handle reduced motion)
      world.burst(mx, my, PALETTE.projPlayer, 2, 70, 0.15, 2)
      world.addShake(t.feel.shakeFire)
      world.bus.emit('shot_fired', {})
    }

    // alt-fire (E / touch HEAVY): the blaster's charge shot — heavier, slower
    // bolt on its own cooldown. It IS the blaster, so requisition mods apply;
    // available even while an armament is held.
    if (intent.interact && ps.altT <= 0) {
      // IMPACT SURCHARGE shortens the heavy-shot cycle
      ps.altT = t.armaments.altFire.cooldown * (world.amenities.has('surcharge') ? t.amenities.surcharge.cooldownMul : 1)
      const muzzle = p.radius + 8
      const bolt = makeProjectile(
        t,
        p.x + intent.aim.x * muzzle,
        p.y + intent.aim.y * muzzle,
        intent.aim.x,
        intent.aim.y,
        t.player.projectileSpeed * t.armaments.altFire.speedMul,
        Math.round(t.player.projectileDamage * world.mods.damageMul * t.armaments.altFire.damageMul),
        'participant'
      )
      bolt.radius = t.armaments.altFire.radius
      world.add(bolt)
      world.burst(p.x + intent.aim.x * muzzle, p.y + intent.aim.y * muzzle, PALETTE.projPlayer, 6, 90, 0.25, 3)
      world.addShake(t.feel.shakeFire * 3)
      world.bus.emit('shot_fired', {})
    }

    // magic: screen-clearing sanctioned resource
    if (intent.magic && ps.magic > 0) {
      ps.magic--
      world.bus.emit('magic_used', { remaining: ps.magic })
      world.addShake(t.feel.shakeBig)
      world.burst(p.x, p.y, PALETTE.magic, 40, 380, 0.9, 4)
      // LINGERING SANCTION: the detonation leaves a damaging field behind
      if (world.amenities.has('lingering')) {
        const lg = t.amenities.lingering
        world.fields.push({ x: p.x, y: p.y, r: lg.radius, t: lg.seconds, tickT: lg.tickInterval })
      }
      for (const e of world.enemies()) {
        if (e.kind === 'boss') {
          // bosses take chip damage from magic, never a clear
          if (e.hp !== undefined) e.hp = Math.max(1, e.hp - t.magic.screenDamage)
          e.flash = t.feel.hitFlashTime
          continue
        }
        if (e.etype === 'auditor') {
          e.dead = true
          world.addScore(t.score.auditorRemoved)
          world.burst(e.x, e.y, PALETTE.auditor, 24, 200, 0.8)
          world.bus.emit('death_type_removed', {})
          continue
        }
        if (e.hp !== undefined) {
          e.hp -= t.magic.screenDamage
          e.flash = t.feel.hitFlashTime
          if (e.hp <= 0) killEntity(world, e, 'magic')
        }
      }
    }

    // tile effects at player center
    const tx = Math.floor(p.x / TILE)
    const ty = Math.floor(p.y / TILE)
    const tile = world.map.at(tx, ty)

    if (tile === Tile.Trap) {
      if ((p.trapT ?? 0) <= 0) {
        hurtPlayer(world, p, t.hazards.trapDamage, 'floor hazard')
        world.bus.emit('trap_hit', {})
        p.trapT = t.hazards.trapCooldown
      } else p.trapT = (p.trapT ?? 0) - dt
    }

    // circuit relay: stepping on it latches it; when the floor's last relay
    // latches, every shutter retracts (single floor-wide circuit)
    if (tile === Tile.Relay) {
      world.map.set(tx, ty, Tile.RelayOn)
      world.burst(p.x, p.y, PALETTE.relay, 14, 160, 0.5)
      let remaining = 0
      for (const _ of world.map.tilesOfType(Tile.Relay)) remaining++
      world.bus.emit('relay_latched', { remaining })
      if (remaining === 0) {
        for (const s of [...world.map.tilesOfType(Tile.Shutter)]) {
          world.map.set(s.x, s.y, Tile.ShutterOpen)
          world.burst(s.x * TILE + TILE / 2, s.y * TILE + TILE / 2, PALETTE.relay, 10, 120, 0.5)
        }
        world.addShake(t.feel.shakeSmall)
        world.bus.emit('shutters_open', {})
      }
    }

    if (isTeleporter(tile)) {
      if ((p.teleT ?? 0) <= 0) {
        const dest = world.map.telePartner(tx, ty)
        if (dest) {
          world.burst(p.x, p.y, PALETTE.teleporter, 16, 200, 0.6)
          p.x = dest.x * TILE + TILE / 2
          p.y = dest.y * TILE + TILE / 2
          p.px = p.x
          p.py = p.y
          world.burst(p.x, p.y, PALETTE.teleporter, 16, 200, 0.6)
          world.bus.emit('teleport', {})
          p.teleT = 1.0
        }
      } else p.teleT = (p.teleT ?? 0) - dt
    } else {
      p.teleT = 0
    }

    if (tile === Tile.Exit && world.exitOpen && !world.cleared) {
      world.cleared = true
      world.bus.emit('level_clear', { floor: world.floor, timeMs: Math.round(world.levelTime * 1000) })
    }
  }
}

/** Fire one trigger-pull of a sanctioned armament (fixed profile, no requisition mods). */
function fireArmament(world: World, id: ArmamentId, mx: number, my: number, ax: number, ay: number): void {
  const t = world.tuning
  if (id === 'scatter') {
    const cfg = t.armaments.scatter
    const base = Math.atan2(ay, ax)
    // deterministic even fan — no RNG in the spread
    for (let i = 0; i < cfg.pellets; i++) {
      const a = base + cfg.spreadRad * (i / (cfg.pellets - 1) - 0.5)
      const pellet = makeProjectile(t, mx, my, Math.cos(a), Math.sin(a), cfg.projectileSpeed, cfg.damage, 'participant')
      pellet.lifetime = cfg.lifetime // short reach is the tradeoff
      world.add(pellet)
    }
  } else if (id === 'lance') {
    const cfg = t.armaments.lance
    const bolt = makeProjectile(t, mx, my, ax, ay, cfg.projectileSpeed, cfg.damage, 'participant')
    bolt.proj!.pierce = true
    bolt.proj!.hitIds = []
    bolt.radius = 5
    world.add(bolt)
  } else if (id === 'seam') {
    const cfg = t.armaments.seam
    const bolt = makeProjectile(t, mx, my, ax, ay, cfg.projectileSpeed, cfg.damage, 'participant')
    bolt.proj!.bounces = cfg.bounces
    world.add(bolt)
  } else {
    const cfg = t.armaments.suppressor
    world.add(makeProjectile(t, mx, my, ax, ay, cfg.projectileSpeed, cfg.damage, 'participant'))
  }
}

/** a tile the player can pass through as a doorway (locked needs a key in hand) */
function doorway(world: World, tx: number, ty: number, keys: number): boolean {
  const t = world.map.at(tx, ty)
  return t === Tile.Door || t === Tile.OpenDoor || (t === Tile.LockedDoor && keys > 0)
}

/**
 * Door-approach assist (playtest: snagging on door frames unless perfectly
 * aligned). When the tile directly ahead on the dominant movement axis is
 * solid but a doorway sits one tile to the side within tolerance — or the
 * tile ahead IS a doorway the circle is grazing off-center — steer the
 * lateral component toward the opening's centerline. Deterministic (pure
 * function of position + intent), player-only: enemy steering is untouched,
 * so dormancy/wake and door-pile behavior cannot regress. Same tile-based
 * scheme as the rounded-corner resolution in TileMap.sweepAxis — this shapes
 * the input to the sweep rather than adding a second collision pass.
 */
function doorAssist(world: World, p: Entity, dx: number, dy: number, dt: number): { x: number; y: number } {
  const cfg = world.tuning.player.doorAssist
  const keys = p.player?.keys ?? 0
  const horizontal = Math.abs(dx) >= Math.abs(dy)
  const step = (toward: number, from: number) => {
    const d = toward - from
    const cap = cfg.pullSpeed * dt
    return Math.max(-cap, Math.min(cap, d))
  }
  if (horizontal && dx !== 0) {
    const tx = Math.floor((p.x + Math.sign(dx) * (p.radius + 4)) / TILE)
    const ty = Math.floor(p.y / TILE)
    const centerOf = (t: number) => t * TILE + TILE / 2
    if (doorway(world, tx, ty, keys)) {
      // grazing the frame of the doorway ahead: pull onto its centerline
      if (Math.abs(centerOf(ty) - p.y) > 0.5) return { x: 0, y: step(centerOf(ty), p.y) }
    } else if (world.map.solidAt(tx, ty)) {
      for (const side of [-1, 1]) {
        if (doorway(world, tx, ty + side, keys) && Math.abs(centerOf(ty + side) - p.y) <= p.radius + cfg.tolerance) {
          return { x: 0, y: step(centerOf(ty + side), p.y) }
        }
      }
    }
  } else if (dy !== 0) {
    const ty = Math.floor((p.y + Math.sign(dy) * (p.radius + 4)) / TILE)
    const tx = Math.floor(p.x / TILE)
    const centerOf = (t: number) => t * TILE + TILE / 2
    if (doorway(world, tx, ty, keys)) {
      if (Math.abs(centerOf(tx) - p.x) > 0.5) return { x: step(centerOf(tx), p.x), y: 0 }
    } else if (world.map.solidAt(tx, ty)) {
      for (const side of [-1, 1]) {
        if (doorway(world, tx + side, ty, keys) && Math.abs(centerOf(tx + side) - p.x) <= p.radius + cfg.tolerance) {
          return { x: step(centerOf(tx + side), p.x), y: 0 }
        }
      }
    }
  }
  return { x: 0, y: 0 }
}

/** Doors open on approach: unlocked freely, locked if a key is held. */
function openAdjacentDoors(world: World, p: Entity, dx: number, dy: number): void {
  const ps = p.player!
  const reach = p.radius + 10
  const cx = p.x + Math.sign(dx) * reach
  const cy = p.y + Math.sign(dy) * reach
  for (const [x, y] of [
    [Math.floor(cx / TILE), Math.floor(p.y / TILE)],
    [Math.floor(p.x / TILE), Math.floor(cy / TILE)]
  ] as const) {
    const tile = world.map.at(x, y)
    if (tile === Tile.Door) {
      world.map.openDoor(x, y)
      world.bus.emit('door_opened', {})
    } else if (tile === Tile.LockedDoor && ps.keys > 0) {
      ps.keys--
      world.map.openDoor(x, y)
      world.bus.emit('key_used', {})
      world.bus.emit('door_unlocked', {})
      world.bus.emit('door_opened', {})
    }
  }
}
